import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ArquivoVisual, FileManagerConfig } from './file-manager.interface';
import { ToastService } from 'src/app/modules/services/toast/toast.service';


@Component({
  selector: 'app-file-manager',
  templateUrl: './file-manager.component.html',
  styleUrls: ['./file-manager.component.scss']
})
export class FileManagerComponent implements OnChanges {
  
  // --- Entradas de Dados ---
  @Input() imagens: ArquivoVisual[] = [];
  @Input() documentos: ArquivoVisual[] = [];
  
  // --- Configuração ---
  @Input() config: FileManagerConfig = {
    permitirUpload: true,
    permitirBaixarTodos: true,
    permitirReiniciar: false,
    modoEdicao: true // true = mostra lixeira, false = mostra download
  };

  @Input() validExtensions: string[] = [
    'pdf', 'doc', 'docx', 'txt', 'odt', 'xlsx', 'xls', 'csv', 'ods', 
    'ppt', 'pptx', 'odp', 'jpeg', 'jpg', 'png', 'zip', 'rar', 'html', 'htm'
  ];

  // --- Saídas de Eventos (Ações) ---
  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() itemAction = new EventEmitter<ArquivoVisual>(); // Dispara download ou delete dependendo do modo
  @Output() downloadAll = new EventEmitter<void>();
  @Output() restart = new EventEmitter<void>();
  
  
  // --- Controle de Paginação Interno ---
  imagensDaPagina: ArquivoVisual[] = [];
  paginaImagem: number = 0;
  paginaDocumento: number = 0;
  
  readonly docPorPagina = 5; // Ajuste conforme necessário
  readonly imgPorPagina = 8;

  // Views fatiadas para o HTML
  imagensViewRow1: ArquivoVisual[] = [];
  imagensViewRow2: ArquivoVisual[] = [];
  documentosView: ArquivoVisual[] = [];

  
  constructor(private toastService: ToastService) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Recalcula as views sempre que a lista de imagens ou documentos mudar
    if (changes['imagens']) {
      this.atualizarViewImagens();
    }
    if (changes['documentos']) {
      this.atualizarViewDocumentos();
    }
  }

  // --- Lógica de Imagens ---
  prevImages() {
    if (this.paginaImagem > 0) {
      this.paginaImagem--;
      this.atualizarViewImagens();
    }
  }

  nextImages() {
    if ((this.paginaImagem + 1) * this.imgPorPagina < this.imagens.length) {
      this.paginaImagem++;
      this.atualizarViewImagens();
    }
  }

  private atualizarViewImagens() {
    const start = this.paginaImagem * this.imgPorPagina;
    const pageImages = this.imagens.slice(start, start + this.imgPorPagina);
    this.imagensDaPagina = this.imagens.slice(start, start + this.imgPorPagina);
    this.imagensViewRow1 = pageImages.slice(0, 4);
    this.imagensViewRow2 = pageImages.slice(4, 8);
  }

  // --- Lógica de Documentos ---
  prevDocuments() {
    if (this.paginaDocumento > 0) {
      this.paginaDocumento--;
      this.atualizarViewDocumentos();
    }
  }

  nextDocuments() {
    const maxPages = Math.ceil(this.documentos.length / this.docPorPagina);
    if (this.paginaDocumento < maxPages - 1) {
      this.paginaDocumento++;
      this.atualizarViewDocumentos();
    }
  }

  private atualizarViewDocumentos() {
    const start = this.paginaDocumento * this.docPorPagina;
    this.documentosView = this.documentos.slice(start, start + this.docPorPagina);
  }

  // --- Handlers de Ação ---
  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const filesToProcess = Array.from(input.files);
    const validFiles: File[] = [];

    filesToProcess.forEach((file) => {
      // 1. Obtém extensão
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      // 2. Validação
      if (!fileExtension || !this.validExtensions.includes(fileExtension)) {
        
        const validFormatsString = this.validExtensions.map(ext => ext.toUpperCase()).join(' | ');
        
        // Dispara o erro (Descomente a linha abaixo quando importar seu serviço)
        this.toastService.showError(`O arquivo "${file.name}" possui um formato inválido. Formatos válidos: ${validFormatsString}.`);
        
        console.warn(`Formato inválido: ${file.name}`); // Fallback para teste
        
        return; // Pula este arquivo específico e vai para o próximo do loop
      }

      // Se passou, adiciona à lista de válidos
      validFiles.push(file);
    });

    // 3. Emite apenas os arquivos válidos para o Pai processar
    if (validFiles.length > 0) {
      this.filesSelected.emit(validFiles);
    }

    // Limpa o input para permitir selecionar o mesmo arquivo novamente se falhar ou se quiser adicionar mais
    input.value = '';
  }

  onItemClick(item: ArquivoVisual) {
    this.itemAction.emit(item);
  }
}